/**
 * Sub Navigation Bar Component
 * Displays sub-categories based on the selected main navigation item
 */

import { useLocation, Link } from 'react-router-dom';
import { Box, Tabs, Tab } from '@mui/material';
import { useState, useEffect } from 'react';

interface SubNavItem {
  label: string;
  path: string;
}

interface SubNavConfig {
  [key: string]: SubNavItem[];
}

// Define sub-navigation items for each main route
const subNavConfig: SubNavConfig = {
  '/admin': [
    { label: 'Dashboard', path: '/admin/dashboard' },
    { label: 'Users', path: '/admin/users' },
    { label: 'Reference Data', path: '/admin/reference-data' },
  ],
  '/broker-accounts': [
    { label: 'Registered Accounts', path: '/broker-accounts' },
    { label: 'Add Account', path: '/broker-accounts/add' },
  ],
  '/orders': [
    { label: 'Order History', path: '/orders/history' },
  ],
  '/portfolio': [
    { label: 'Overview', path: '/portfolio' },
    { label: 'Holdings', path: '/portfolio/holdings' },
  ],
  '/ai-assist': [
    { label: 'Chat', path: '/ai-assist' },
  ],
  '/analytics': [
    { label: 'Dashboard', path: '/analytics' },
    { label: 'Reports', path: '/analytics/reports' },
    { label: 'Charts', path: '/analytics/charts' },
  ],
  '/profile': [
    { label: 'Profile', path: '/profile' },
    { label: 'Preferences', path: '/profile/preferences' },
  ],
};

export function SubNavigationBar() {
  const location = useLocation();
  const [persistedMainRoute, setPersistedMainRoute] = useState<string | null>(null);

  // Find which main route we're on
  const mainRoute = Object.keys(subNavConfig).find((route) =>
    location.pathname.startsWith(route)
  );

  // Persist the main route if we're on a valid main route
  useEffect(() => {
    if (mainRoute) {
      setPersistedMainRoute(mainRoute);
    }
  }, [mainRoute]);

  // Use persisted route if current route doesn't match, but only if we were previously on a valid route
  const activeMainRoute = mainRoute || persistedMainRoute;

  // Get sub-nav items for current route
  const subNavItems = activeMainRoute ? subNavConfig[activeMainRoute] : [];

  // Compute current tab directly from location.pathname (no state needed)
  const getCurrentTab = (): number => {
    if (subNavItems.length === 0) return 0;

    // Find the matching tab index
    let matchedIndex = -1;

    // First, try exact match
    matchedIndex = subNavItems.findIndex((item) => location.pathname === item.path);

    // If no exact match, try prefix match (but avoid matching the main route itself)
    if (matchedIndex < 0) {
      matchedIndex = subNavItems.findIndex((item) => {
        if (item.path === activeMainRoute) {
          // For the base route, only match if pathname exactly matches
          return location.pathname === item.path;
        }
        // For sub-routes, check if pathname starts with the item path
        return location.pathname.startsWith(item.path + '/') || location.pathname === item.path;
      });
    }

    // If still no match and we're on the main route, default to first tab
    if (matchedIndex < 0 && location.pathname === activeMainRoute) {
      matchedIndex = 0;
    }

    return matchedIndex >= 0 ? matchedIndex : 0;
  };

  const currentTab = getCurrentTab();

  // Don't render if no sub-navigation items for current route
  if (!activeMainRoute || subNavItems.length === 0) {
    return null;
  }

  const handleTabChange = (_event: React.SyntheticEvent, _newValue: number) => {
    // This is handled by Link navigation, but we keep it for Tabs component compatibility
  };

  return (
    <Box
      sx={{
        background: '#ffffff',
        borderBottom: '1px solid rgba(4, 55, 123, 0.2)',
        border: '1px solid',
        borderColor: 'rgba(4, 55, 123, 0.3)',
        borderRadius: '8px', // Rounded corners on all sides
        px: { xs: 2, md: 3 },
        backdropFilter: 'blur(10px)',
        boxShadow: `
          0 0 0 1px rgba(4, 55, 123, 0.15),
          0 0 8px rgba(4, 55, 123, 0.2),
          0 0 15px rgba(4, 55, 123, 0.15),
          inset 0 0 10px rgba(255, 255, 255, 0.03),
          inset 0 1px 0 rgba(255, 255, 255, 0.1),
          inset 0 -1px 0 rgba(4, 55, 123, 0.1)
        `,
      }}
    >
      <Tabs
        value={currentTab}
        onChange={handleTabChange}
        sx={{
          minHeight: 48,
          '& .MuiTab-root': {
            textTransform: 'none',
            color: '#04377b',
            minHeight: 48,
            fontSize: '0.875rem',
            fontWeight: 500,
            position: 'relative',
            '&.Mui-selected': {
              color: '#04377b',
              fontWeight: 600,
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '3px',
                backgroundColor: '#04377b',
                borderRadius: '3px 3px 0 0',
              },
            },
          },
          '& .MuiTabs-indicator': {
            display: 'none', // Hide default indicator, using custom underline
          },
        }}
      >
        {subNavItems.map((item, index) => (
          <Tab
            key={item.path}
            label={item.label}
            component={Link}
            to={item.path}
            value={index}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(4, 55, 123, 0.05)',
              },
            }}
          />
        ))}
      </Tabs>
    </Box>
  );
}

